/**
 * The glue behind the Citation Style Converter: a reference list in one style, rewritten
 * in another by way of the records the references describe.
 *
 * A formatted reference cannot be re-styled by moving punctuation around — the style it
 * came in has already thrown away what the target style needs (italics, full given
 * names, issue numbers). So the converter goes back to the source: it matches each
 * reference to its Crossref record (the DOI Finder's matcher), lets the reader confirm
 * the match, fetches the record through CitationEasy's own `/api/cite` route and
 * formats it with the same formatter the citation generators use. A reference that
 * cannot be matched is not guessed at; it is flagged for manual entry.
 *
 * Everything here is pure or takes its network from `fetch`, so the tests drive it with
 * stubs and never touch a real service.
 */

import { formatEntries } from "@/lib/restyle";
import { type CiteEntry, citeLookup } from "@/lib/tool-kit/identifiers/lookup";
import {
  type BestMatch,
  bestMatch,
  hasDoi,
  parseCrossrefItems,
  tooShort,
} from "@/lib/tool-kit/reference-tools/doi-finder";
import type { FetchFailure } from "@/lib/tool-kit/reference-tools/network";
import { runPool } from "@/lib/tool-kit/reference-tools/network";
import {
  findDoi,
  splitReferences,
} from "@/lib/tool-kit/reference-tools/references";

/* -------------------------------------------------------------- styles */

export type StyleId =
  | "apa"
  | "mla"
  | "chicagoAuthorDate"
  | "chicagoNotes"
  | "harvard"
  | "ieee"
  | "vancouver"
  | "ama"
  | "asa"
  | "nature";

export interface StyleOption {
  id: StyleId;
  /** Slug `/api/cite` resolves (a landing-page slug or a catalog slug). */
  slug: string;
  /** The style numbers its references, so the list keeps its order. */
  numbered: boolean;
}

export const STYLE_OPTIONS: readonly StyleOption[] = [
  { id: "apa", slug: "apa", numbered: false },
  { id: "mla", slug: "mla", numbered: false },
  { id: "chicagoAuthorDate", slug: "chicago", numbered: false },
  { id: "chicagoNotes", slug: "chicago-notes-bibliography", numbered: false },
  { id: "harvard", slug: "harvard", numbered: false },
  { id: "ieee", slug: "ieee", numbered: true },
  { id: "vancouver", slug: "vancouver", numbered: true },
  { id: "ama", slug: "ama", numbered: true },
  { id: "asa", slug: "asa", numbered: false },
  { id: "nature", slug: "nature", numbered: true },
];

export function styleOption(id: string): StyleOption {
  return STYLE_OPTIONS.find((option) => option.id === id) ?? STYLE_OPTIONS[0];
}

/* ---------------------------------------------------------------- rows */

/** How many references one run searches: Crossref allows one list query a second. */
export const MAX_REFERENCES = 25;

export type RowState =
  | "queued"
  | "given"
  | "done"
  | "short"
  | "over"
  | "failed";

export interface RefRow {
  id: number;
  reference: string;
  state: RowState;
  /** The Crossref record that best fits the reference; null when none is close. */
  match: BestMatch | null;
  /** The DOI the reference already carries, when `state` is "given". */
  givenDoi: string | null;
  /** The reader's decision: use this DOI. */
  accepted: boolean;
  reason?: FetchFailure;
}

/** One row per reference. A reference that already has a DOI skips the search. */
export function planRows(text: string): RefRow[] {
  let queued = 0;
  return splitReferences(text).map((reference, id): RefRow => {
    const base = {
      id,
      reference,
      match: null,
      givenDoi: null,
      accepted: false,
    };
    if (hasDoi(reference)) {
      return {
        ...base,
        state: "given",
        givenDoi: findDoi(reference),
        accepted: true,
      };
    }
    if (tooShort(reference)) {
      return { ...base, state: "short" };
    }
    queued += 1;
    return { ...base, state: queued > MAX_REFERENCES ? "over" : "queued" };
  });
}

/**
 * The row once Crossref has answered: its best-fitting record, ticked when the match is
 * confident. An uncertain match is shown but left for the reader to tick.
 */
export function withMatch(row: RefRow, body: unknown): RefRow {
  const match = bestMatch(row.reference, parseCrossrefItems(body));
  return {
    ...row,
    state: "done",
    match,
    accepted: match?.level === "confident",
  };
}

/** The DOI this row will be converted from, or null when it is not going to be. */
export function rowDoi(row: RefRow): string | null {
  if (!row.accepted) {
    return null;
  }
  if (row.state === "given") {
    return row.givenDoi;
  }
  return row.state === "done" && row.match ? row.match.candidate.doi : null;
}

/* ------------------------------------------------------ manual entry */

export type GeneratorId =
  | "journal"
  | "book"
  | "chapter"
  | "website"
  | "general";

export const GENERATOR_HREFS: Record<GeneratorId, string> = {
  journal: "/tools/journal-article-citation-generator",
  book: "/tools/book-citation-generator",
  chapter: "/tools/book-chapter-citation-generator",
  website: "/tools/website-citation-generator",
  general: "/",
};

const RE_CHAPTER = /\bIn\s+.+\((?:Eds?|eds?)\.?\)|,\s*edited by\b/;
const RE_JOURNAL =
  /\b\d{1,4}\s*\(\d{1,4}\)\s*,\s*\d|\bvol\.\s*\d|\b(?:19|20)\d{2};\s*\d+(?:\(\d+\))?:\s*\d/i;
const RE_BOOK =
  /\b(?:Press|Publishers?|Publishing|Books|Routledge|Springer|Wiley|Pearson|Sage|McGraw|Penguin|Macmillan)\b|\b\d{1,2}(?:st|nd|rd|th)\s+ed\./i;
const RE_URL = /\b(?:https?:\/\/|www\.)\S+/i;

/**
 * Which citation generator fits a reference the matcher could not place. A guess from
 * the shape of the text — volume and issue numbers mean an article, a publisher means a
 * book — offered as a link, never acted on.
 */
export function suggestGenerator(reference: string): GeneratorId {
  if (RE_CHAPTER.test(reference)) {
    return "chapter";
  }
  if (RE_JOURNAL.test(reference)) {
    return "journal";
  }
  if (RE_BOOK.test(reference)) {
    return "book";
  }
  return RE_URL.test(reference) ? "website" : "general";
}

/* -------------------------------------------------------- conversion */

export type ConvertFailure = FetchFailure | "blocked" | "format";

export type ConvertResult =
  | { ok: true; text: string }
  | { ok: false; reason: ConvertFailure };

const RE_WHITESPACE = /\s+/g;
const RE_LABEL = /^(\[|\()?(\d+)([\].)])?(\s+)/;
const RE_LEADING_MARKS = /^[^\p{L}\p{N}]+/u;

/** One formatted reference as a single tidy line. */
export function tidyCitation(text: string): string {
  return text.replace(RE_WHITESPACE, " ").trim();
}

/** A numbered style's label ("[1]", "1.") rewritten to the reference's place in the list. */
export function renumber(text: string, position: number): string {
  return text.replace(
    RE_LABEL,
    (_match, open?: string, _number?: string, close?: string, space?: string) =>
      `${open ?? ""}${position}${close ?? ""}${space ?? " "}`
  );
}

function sortKey(text: string): string {
  return text.replace(RE_LEADING_MARKS, "").toLowerCase();
}

/** The converted references as one list: sorted for author-date styles, numbered otherwise. */
export function assembleList(
  texts: readonly string[],
  style: StyleOption,
  sort: boolean
): string[] {
  const tidy = texts.map(tidyCitation);
  if (style.numbered) {
    return tidy.map((text, index) => renumber(text, index + 1));
  }
  return sort
    ? [...tidy].sort((a, b) => sortKey(a).localeCompare(sortKey(b)))
    : tidy;
}

export interface ConvertOptions {
  signal: AbortSignal;
  /** Called after each record is fetched, with how many are done. */
  onProgress?: (done: number, total: number) => void;
  /** Injected for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

/** The distinct DOIs to fetch, in first-seen order, keyed by their lower-case form. */
function distinctDois(rows: readonly RefRow[]): Map<string, string> {
  const dois = new Map<string, string>();
  for (const row of rows) {
    const doi = rowDoi(row);
    if (doi && !dois.has(doi.toLowerCase())) {
      dois.set(doi.toLowerCase(), doi);
    }
  }
  return dois;
}

/**
 * Convert every confirmed row to `style`: fetch each distinct DOI's record through
 * `/api/cite`, then format the records in one batch through the same route. Resolves to
 * a result per row id; a row that was not confirmed has none. Rejects only when
 * `options.signal` aborts.
 */
export async function convertRows(
  rows: readonly RefRow[],
  style: StyleOption,
  options: ConvertOptions
): Promise<Map<number, ConvertResult>> {
  const dois = distinctDois(rows);
  const keys = [...dois.keys()];
  const records = new Map<string, CiteEntry | ConvertFailure>();
  await runPool(
    keys,
    async (key) => {
      const lookup = await citeLookup(dois.get(key) ?? key, options.signal);
      records.set(key, lookup.ok ? lookup.value : lookup.reason);
    },
    {
      concurrency: 2,
      signal: options.signal,
      onProgress: (done) => options.onProgress?.(done, keys.length),
    }
  );
  if (options.signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const fetched = keys.flatMap((key) => {
    const record = records.get(key);
    return record && typeof record === "object" ? [{ key, record }] : [];
  });
  const formatted = await formatEntries(
    fetched.map((item) => item.record),
    style.slug,
    { signal: options.signal, fetchImpl: options.fetchImpl }
  );
  const texts = new Map<string, string | null>();
  fetched.forEach((item, index) => {
    texts.set(item.key, formatted[index] ?? null);
  });

  const results = new Map<number, ConvertResult>();
  for (const row of rows) {
    const doi = rowDoi(row);
    if (!doi) {
      continue;
    }
    const key = doi.toLowerCase();
    const record = records.get(key);
    const text = texts.get(key);
    if (typeof record === "string") {
      results.set(row.id, { ok: false, reason: record });
    } else if (text) {
      results.set(row.id, { ok: true, text });
    } else {
      results.set(row.id, { ok: false, reason: "format" });
    }
  }
  return results;
}

/** The converted references, in row order, ready for `assembleList`. */
export function convertedTexts(
  rows: readonly RefRow[],
  results: ReadonlyMap<number, ConvertResult>
): string[] {
  return rows.flatMap((row) => {
    const result = results.get(row.id);
    return result?.ok ? [result.text] : [];
  });
}
